import { Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { ArrowRight, Check, X, ExternalLink } from 'lucide-react'
import { breadcrumbList, faqPage, itemList, jsonLdScript, webPage } from '@/lib/schema'
import { StartFreeButton } from '@/components/marketing/free-plan-cta'
import { useFreePlan } from '@/hooks/use-free-plan'

export interface Alternative {
  name: string
  description: string
  pricing: string
  pros: string[]
  cons: string[]
  url: string
  isUptrack?: boolean
}

export interface ComparisonRow {
  tool: string
  freePlan: string
  checkInterval: string
  alertConfirmation: string
  startingPrice: string
}

export interface AlternativesData {
  tool: string
  slug: string
  intro: string
  /** Optional link to a longer guide, shown under the intro. */
  readMore?: { label: string; href: string }
  alternatives: Alternative[]
  comparisonTable: ComparisonRow[]
}

export function alternativesHead(data: AlternativesData) {
  const title = `Best ${data.tool} Alternatives in 2026 | Uptrack`
  const description = data.intro.slice(0, 155).replace(/\n/g, ' ')
  const listUrl = `https://uptrack.app/alternatives/${data.slug}`

  return {
    meta: [
      { title },
      { name: 'description', content: description },
      { property: 'og:title', content: title },
      { property: 'og:description', content: description },
      { property: 'og:image', content: 'https://uptrack.app/og/alternatives-tool.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: listUrl },
    ],
    scripts: [
      jsonLdScript(webPage({ name: title, description, url: listUrl })),
      jsonLdScript(breadcrumbList([
        { name: 'Home', url: 'https://uptrack.app' },
        { name: 'Alternatives', url: 'https://uptrack.app/alternatives' },
        { name: `${data.tool} Alternatives`, url: listUrl },
      ])),
      jsonLdScript(itemList(
        `Best ${data.tool} Alternatives in 2026`,
        data.alternatives.map((a) => ({ name: a.name, url: a.url, description: a.description })),
        listUrl,
      )),
      // Only questions the visible page answers: the list below and the intro (FAQ markup must match
      // what the visitor reads, and the markdown copy has no <script>).
      jsonLdScript(faqPage([
        {
          question: `What are the best ${data.tool} alternatives?`,
          answer: data.alternatives.slice(0, 5).map((a) => a.name).join(', '),
        },
        {
          question: `Why switch from ${data.tool}?`,
          answer: data.intro.replace(/\n/g, ' '),
        },
      ])),
    ],
  }
}

export function AlternativesPage({ data }: { data: AlternativesData }) {
  const free = useFreePlan()
  return (
    <>
        {/* Hero */}
        <section>
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h1 className="max-w-3xl text-[2.25rem] font-semibold leading-[1.1] tracking-tight md:text-[3rem]">
              Best {data.tool} Alternatives in 2026
            </h1>
            <p className="mt-5 max-w-2xl text-lead text-body">
              {data.intro}
            </p>
            {data.readMore && (
              <p className="mt-4">
                <a href={data.readMore.href} className="font-medium text-primary underline-offset-4 hover:underline">
                  {data.readMore.label}
                </a>
              </p>
            )}
          </div>
        </section>

        {/* Alternatives list */}
        <section className="border-t border-border">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <ol className="divide-y divide-border border-y border-border">
              {data.alternatives.map((alt, i) => (
                <li
                  key={alt.name}
                  className={`grid gap-x-10 gap-y-4 py-8 lg:grid-cols-12 ${alt.isUptrack ? 'bg-card lg:px-6' : ''}`}
                >
                  <div className="lg:col-span-4">
                    <h2 className="flex items-baseline gap-2 text-xl font-semibold tracking-tight">
                      <span className="num text-sm text-muted-foreground">{i + 1}.</span>
                      {alt.name}
                    </h2>
                    {alt.isUptrack && <p className="mt-1 text-xs font-medium text-primary">Our pick</p>}
                    <p className="mt-1 text-sm text-muted-foreground">{alt.pricing}</p>
                    <a
                      href={alt.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${alt.name} website`}
                      className="mt-3 inline-flex items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-primary"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Visit site
                    </a>
                  </div>

                  <div className="lg:col-span-8">
                    <p className="text-[15px] leading-relaxed text-body">
                      {alt.description}
                    </p>

                    <div className="mt-5 grid gap-6 sm:grid-cols-2">
                      <div>
                        <h3 className="mb-2 text-xs font-medium text-muted-foreground">Pros</h3>
                        <ul className="space-y-1.5">
                          {alt.pros.map((pro) => (
                            <li key={pro} className="flex items-start gap-2 text-[15px] text-body">
                              <Check aria-hidden="true" className="mt-1 h-3.5 w-3.5 shrink-0 text-status-up" />
                              {pro}
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h3 className="mb-2 text-xs font-medium text-muted-foreground">Cons</h3>
                        <ul className="space-y-1.5">
                          {alt.cons.map((con) => (
                            <li key={con} className="flex items-start gap-2 text-[15px] text-body">
                              <X aria-hidden="true" className="mt-1 h-3.5 w-3.5 shrink-0 text-status-down" />
                              {con}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Quick comparison table */}
        <section className="border-t border-border">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <h2 className="mb-6 text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Quick Comparison</h2>
            <div className="overflow-x-auto" tabIndex={0} role="region" aria-label="Scrollable table">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-border text-xs text-muted-foreground">
                    <th className="pb-3 pr-4 font-medium">Tool</th>
                    <th className="pb-3 pr-4 font-medium">Entry Plan</th>
                    <th className="pb-3 pr-4 font-medium">Check Interval</th>
                    <th className="pb-3 pr-4 font-medium">Alert Confirmation</th>
                    <th className="pb-3 font-medium">Starting Price</th>
                  </tr>
                </thead>
                <tbody>
                  {data.comparisonTable.map((row) => (
                    <tr
                      key={row.tool}
                      className={`border-b border-border last:border-0 ${
                        row.tool === 'Uptrack' ? 'bg-card font-medium text-foreground' : 'text-body'
                      }`}
                    >
                      <td className="py-3 pr-4">{row.tool}</td>
                      <td className="py-3 pr-4">{row.freePlan}</td>
                      <td className="py-3 pr-4 tabular-nums">{row.checkInterval}</td>
                      <td className="py-3 pr-4">{row.alertConfirmation}</td>
                      <td className="py-3 tabular-nums">{row.startingPrice}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="border-t border-border">
          <div className="container mx-auto px-4 py-12 md:py-16">
            <div className="grid items-center gap-6 lg:grid-cols-12">
              <div className="lg:col-span-8">
                <h2 className="text-[1.75rem] font-semibold leading-tight tracking-tight md:text-headline">Try Uptrack</h2>
                <p className="mt-3 max-w-lg text-lead text-body">
                  {free.isOpen
                    ? 'Start free with 5 monitors, or choose Pro, Team, or Business, for 30-second monitoring and alert confirmation.'
                    : 'Choose Pro, Team, or Business for 30-second monitoring and alert confirmation.'}
                </p>
              </div>
              <div className="flex flex-wrap items-center gap-3 lg:col-span-4 lg:justify-end">
                <StartFreeButton />
                <Button size="lg" variant={free.isOpen ? 'outline' : 'default'} className="h-11 px-5 text-[15px]" asChild>
                  <Link to="/pricing">
                    See plans
                    {!free.isOpen && <ArrowRight className="ml-1 h-4 w-4" aria-hidden="true" />}
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
    </>
  )
}
